import React, { useRef } from 'react';
import VideoCard from './VideoCard';
import { InFeedAd } from './AdBanner';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const VideoRow = ({ title, videos, loading = false, showProgress = false, showAds = true, adFrequency = 5 }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 360;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="mb-12">
        <h2 className="section-header">{title}</h2>
        <div className="horizontal-scroll">
          {[...Array(6)].map((_, i) => (
            <div key={`skeleton-${title}-${i}`} className="w-[320px] flex-shrink-0">
              <div className="aspect-video skeleton mb-3" />
              <div className="h-4 skeleton w-3/4 mb-2" />
              <div className="h-3 skeleton w-1/2" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!videos || videos.length === 0) {
    return null;
  }

  // Insert ads between videos
  const contentWithAds = [];
  videos.forEach((video, index) => {
    contentWithAds.push({ type: 'video', data: video, index });
    // Insert ad after every adFrequency videos (but not after the last video)
    if (showAds && (index + 1) % adFrequency === 0 && index < videos.length - 1) {
      contentWithAds.push({ type: 'ad', index: Math.floor(index / adFrequency) });
    }
  });

  return (
    <section className="mb-12 group/row" data-testid={`video-row-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2 className="section-header">{title}</h2>
      
      <div className="relative">
        {/* Left scroll button */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/80 backdrop-blur rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity hover:bg-white/20 -translate-x-1/2"
          data-testid={`scroll-left-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        {/* Video scroll container */}
        <div 
          ref={scrollRef}
          className="horizontal-scroll"
        >
          {contentWithAds.map((item, idx) => (
            item.type === 'video' ? (
              <div 
                key={`${title}-video-${item.data.id || idx}`} 
                className="w-[320px] flex-shrink-0"
                style={{ animationDelay: `${item.index * 0.1}s` }}
              >
                <VideoCard 
                  video={item.data} 
                  showProgress={showProgress}
                  progress={item.data.progress}
                />
              </div>
            ) : (
              <div 
                key={`${title}-ad-${item.index}`} 
                className="w-[320px] flex-shrink-0"
              >
                <InFeedAd index={item.index} />
              </div>
            )
          ))}
        </div>

        {/* Right scroll button */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/80 backdrop-blur rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity hover:bg-white/20 translate-x-1/2"
          data-testid={`scroll-right-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
    </section>
  );
};

export default VideoRow;
